import { useCallback, useEffect, useState, useRef } from 'react';
import { GoogleMap, Marker, Polyline, InfoWindow, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import { Loader2, Search, Navigation } from 'lucide-react';
import type { Coordinate } from '@/types/winchStartPoint';

/**
 * RouteMap Component
 * Displays an interactive Google Map with route visualization
 * Shows start point, pickup, and destination with markers and route line
 */

// Define libraries as a constant outside component to prevent reloads
const libraries: ("places")[] = ['places'];

interface RouteMapProps {
  startPoint: {
    name: string;
    coordinates: Coordinate;
  };
  pickup: {
    name: string;
    coordinates: Coordinate;
  };
  destination: {
    name: string;
    coordinates: Coordinate;
  };
  height?: string;
  className?: string;
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultMapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'all',
      elementType: 'geometry',
      stylers: [{ color: '#242f3e' }],
    },
    {
      featureType: 'all',
      elementType: 'labels.text.stroke',
      stylers: [{ color: '#242f3e' }],
    },
    {
      featureType: 'all',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#746855' }],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#17263c' }],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [{ color: '#38414e' }],
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [{ color: '#212a37' }],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [{ color: '#746855' }],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [{ color: '#1f2835' }],
    },
  ],
};

export function RouteMap({
  startPoint,
  pickup,
  destination,
  height = '400px',
  className = '',
}: RouteMapProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<'start' | 'pickup' | 'destination' | null>(null);
  const [boundsListener, setBoundsListener] = useState<google.maps.MapsEventListener | null>(null);
  const [totalDistance, setTotalDistance] = useState<string>('');
  const [totalDuration, setTotalDuration] = useState<string>('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [searchBox, setSearchBox] = useState<google.maps.places.Autocomplete | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Route segments with different colors
  const [leg1Path, setLeg1Path] = useState<google.maps.LatLng[]>([]);
  const [leg2Path, setLeg2Path] = useState<google.maps.LatLng[]>([]);
  const [leg1Info, setLeg1Info] = useState<{ distance: string; duration: string } | null>(null);
  const [leg2Info, setLeg2Info] = useState<{ distance: string; duration: string } | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<'leg1' | 'leg2' | null>(null);
  const [routeInfoPosition, setRouteInfoPosition] = useState<google.maps.LatLng | null>(null);

  // Fit bounds to show all markers
  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
    
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(startPoint.coordinates);
    bounds.extend(pickup.coordinates);
    bounds.extend(destination.coordinates);
    
    map.fitBounds(bounds);
    
    // Add some padding
    const listener = window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      const currentZoom = map.getZoom();
      if (currentZoom && currentZoom > 15) {
        map.setZoom(15);
      }
    });
    
    setBoundsListener(listener);
  }, [startPoint.coordinates, pickup.coordinates, destination.coordinates]);

  const onUnmount = useCallback(() => {
    if (boundsListener) {
      window.google.maps.event.removeListener(boundsListener);
    }
    setMap(null);
  }, [boundsListener]);

  // Calculate route using Directions API
  useEffect(() => {
    if (!isLoaded || !window.google) return;

    const calculateRoute = async () => {
      setIsCalculating(true);
      try {
        const directionsService = new window.google.maps.DirectionsService();

        // Request route from start → pickup → destination
        const result = await directionsService.route({
          origin: startPoint.coordinates,
          destination: destination.coordinates,
          waypoints: [
            {
              location: pickup.coordinates,
              stopover: true,
            },
          ],
          travelMode: window.google.maps.TravelMode.DRIVING,
          optimizeWaypoints: false, // Keep the order: start → pickup → destination
        });

        // Extract route legs
        const legs = result.routes[0].legs;
        
        // Leg 1: Start → Pickup
        if (legs[0]) {
          const path1: google.maps.LatLng[] = [];
          legs[0].steps.forEach(step => {
            step.path.forEach(point => path1.push(point));
          });
          setLeg1Path(path1);
          setLeg1Info({
            distance: legs[0].distance?.text || '',
            duration: legs[0].duration?.text || '',
          });
        }

        // Leg 2: Pickup → Destination
        if (legs[1]) {
          const path2: google.maps.LatLng[] = [];
          legs[1].steps.forEach(step => {
            step.path.forEach(point => path2.push(point));
          });
          setLeg2Path(path2);
          setLeg2Info({
            distance: legs[1].distance?.text || '',
            duration: legs[1].duration?.text || '',
          });
        }

        // Calculate total distance and duration
        let totalDistanceMeters = 0;
        let totalDurationSeconds = 0;

        legs.forEach((leg) => {
          if (leg.distance) totalDistanceMeters += leg.distance.value;
          if (leg.duration) totalDurationSeconds += leg.duration.value;
        });

        const distanceKm = (totalDistanceMeters / 1000).toFixed(2);
        const durationMinutes = Math.round(totalDurationSeconds / 60);

        setTotalDistance(`${distanceKm} كم`);
        setTotalDuration(`${durationMinutes} دقيقة`);
      } catch (error) {
        console.error('Error calculating route:', error);
      } finally {
        setIsCalculating(false);
      }
    };

    calculateRoute();
  }, [isLoaded, startPoint.coordinates, pickup.coordinates, destination.coordinates]);

  // Handle search place selection
  const onPlaceChanged = () => {
    if (searchBox && map) {
      const place = searchBox.getPlace();
      if (place.geometry?.location) {
        map.panTo(place.geometry.location);
        map.setZoom(15);
      }
    }
  };

  // Handle autocomplete load
  const onLoadAutocomplete = (autocomplete: google.maps.places.Autocomplete) => {
    setSearchBox(autocomplete);
  };

  if (loadError) {
    return (
      <div 
        className={`flex items-center justify-center bg-danger/10 border border-danger/30 rounded-lg ${className}`}
        style={{ height }}
      >
        <div className="text-center text-danger p-6">
          <p className="font-bold mb-2">⚠️ فشل تحميل الخريطة</p>
          <p className="text-sm">
            {apiKey ? 'تحقق من مفتاح Google Maps API' : 'لم يتم تكوين مفتاح Google Maps API'}
          </p>
          <p className="text-xs mt-2 text-gray-400" dir="ltr">
            {loadError.message}
          </p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div 
        className={`flex items-center justify-center bg-dark-lighter border border-gray-700 rounded-lg ${className}`}
        style={{ height }}
      >
        <div className="text-center text-gray-400">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
          <p>جاري تحميل الخريطة...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Distance and Duration Info */}
      {(totalDistance || isCalculating) && (
        <div className="bg-gradient-to-r from-dark-lighter to-dark border border-primary/30 rounded-lg p-4 mb-4 shadow-lg">
          <div className="flex items-center justify-between gap-4" dir="rtl">
            <div className="flex items-center gap-3">
              <Navigation className="h-5 w-5 text-primary" />
              <span className="text-gray-300">المسافة الكلية:</span>
              <span className="text-primary font-bold text-xl">
                {isCalculating ? (
                  <Loader2 className="h-5 w-5 animate-spin inline" />
                ) : (
                  totalDistance
                )}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 text-success" />
              <span className="text-gray-300">الوقت المتوقع:</span>
              <span className="text-success font-bold text-xl">
                {isCalculating ? (
                  <Loader2 className="h-5 w-5 animate-spin inline" />
                ) : (
                  totalDuration
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="relative rounded-lg overflow-hidden border border-gray-700 shadow-2xl" style={{ height }}>
        {/* Search Box Overlay */}
        {isLoaded && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 w-full max-w-md px-4">
            <Autocomplete
              onLoad={onLoadAutocomplete}
              onPlaceChanged={onPlaceChanged}
            >
              <div className="relative">
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="ابحث عن موقع..."
                  className="w-full pr-10 pl-4 py-3 bg-dark-lighter/95 backdrop-blur-sm border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent shadow-lg"
                  dir="rtl"
                />
              </div>
            </Autocomplete>
          </div>
        )}

        {/* Legend */}
        <div className="absolute bottom-4 right-4 z-10 bg-dark-lighter/95 backdrop-blur-sm border border-gray-600 rounded-lg p-3 shadow-lg" dir="rtl">
          <div className="space-y-2 text-sm">
            <div className="text-xs text-gray-400 mb-2 font-bold">المواقع:</div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-blue-500 border-2 border-white"></div>
              <span className="text-gray-200">نقطة البداية</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white"></div>
              <span className="text-gray-200">منطقة الاستلام</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-orange-500 border-2 border-white"></div>
              <span className="text-gray-200">منطقة التوصيل</span>
            </div>
            <div className="border-t border-gray-600 my-2"></div>
            <div className="text-xs text-gray-400 mb-2 font-bold">الطرق:</div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-1 bg-cyan-400"></div>
              <span className="text-gray-200">البداية → الاستلام</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-1 bg-purple-400"></div>
              <span className="text-gray-200">الاستلام → التوصيل</span>
            </div>
          </div>
        </div>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={12}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={defaultMapOptions}
        >
          {/* Leg 1: Start → Pickup (Cyan/Blue) */}
          {leg1Path.length > 0 && (
            <Polyline
              path={leg1Path}
              options={{
                strokeColor: '#00D9FF',
                strokeOpacity: selectedRoute === 'leg1' ? 1 : 0.8,
                strokeWeight: selectedRoute === 'leg1' ? 7 : 5,
                geodesic: true,
                clickable: true,
              }}
              onClick={(e) => {
                setSelectedRoute('leg1');
                setSelectedMarker(null);
                if (e.latLng) {
                  setRouteInfoPosition(e.latLng);
                }
              }}
            />
          )}

          {/* Leg 2: Pickup → Destination (Purple/Magenta) */}
          {leg2Path.length > 0 && (
            <Polyline
              path={leg2Path}
              options={{
                strokeColor: '#A855F7',
                strokeOpacity: selectedRoute === 'leg2' ? 1 : 0.8,
                strokeWeight: selectedRoute === 'leg2' ? 7 : 5,
                geodesic: true,
                clickable: true,
              }}
              onClick={(e) => {
                setSelectedRoute('leg2');
                setSelectedMarker(null);
                if (e.latLng) {
                  setRouteInfoPosition(e.latLng);
                }
              }}
            />
          )}

        {/* Start Point Marker (Blue) - Enhanced */}
        <Marker
          position={startPoint.coordinates}
          title={startPoint.name}
          onClick={() => setSelectedMarker('start')}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: '#3B82F6',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 3,
          }}
          label={{
            text: 'S',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          animation={selectedMarker === 'start' ? window.google.maps.Animation.BOUNCE : undefined}
        />

        {/* Pickup Location Marker (Green) - Enhanced */}
        <Marker
          position={pickup.coordinates}
          title={pickup.name}
          onClick={() => setSelectedMarker('pickup')}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: '#10B981',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 3,
          }}
          label={{
            text: 'P',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          animation={selectedMarker === 'pickup' ? window.google.maps.Animation.BOUNCE : undefined}
        />

        {/* Destination Marker (Orange) - Enhanced */}
        <Marker
          position={destination.coordinates}
          title={destination.name}
          onClick={() => setSelectedMarker('destination')}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: '#F59E0B',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 3,
          }}
          label={{
            text: 'D',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          animation={selectedMarker === 'destination' ? window.google.maps.Animation.BOUNCE : undefined}
        />

        {/* Info Windows - Enhanced Design */}
        {selectedMarker === 'start' && (
          <InfoWindow
            position={startPoint.coordinates}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div className="p-3 min-w-[200px]" dir="rtl">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <h3 className="font-bold text-blue-600 text-lg">نقطة البداية</h3>
              </div>
              <p className="text-gray-800 font-semibold mb-2">{startPoint.name}</p>
              <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded" dir="ltr">
                📍 {startPoint.coordinates.lat.toFixed(6)}, {startPoint.coordinates.lng.toFixed(6)}
              </div>
            </div>
          </InfoWindow>
        )}

        {selectedMarker === 'pickup' && (
          <InfoWindow
            position={pickup.coordinates}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div className="p-3 min-w-[200px]" dir="rtl">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <h3 className="font-bold text-green-600 text-lg">منطقة الاستلام</h3>
              </div>
              <p className="text-gray-800 font-semibold mb-2">{pickup.name}</p>
              <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded" dir="ltr">
                📍 {pickup.coordinates.lat.toFixed(6)}, {pickup.coordinates.lng.toFixed(6)}
              </div>
            </div>
          </InfoWindow>
        )}

        {selectedMarker === 'destination' && (
          <InfoWindow
            position={destination.coordinates}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div className="p-3 min-w-[200px]" dir="rtl">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                <h3 className="font-bold text-orange-600 text-lg">منطقة التوصيل</h3>
              </div>
              <p className="text-gray-800 font-semibold mb-2">{destination.name}</p>
              <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded" dir="ltr">
                📍 {destination.coordinates.lat.toFixed(6)}, {destination.coordinates.lng.toFixed(6)}
              </div>
            </div>
          </InfoWindow>
        )}

        {/* Route Segment Info Window */}
        {selectedRoute && routeInfoPosition && (
          <InfoWindow
            position={routeInfoPosition}
            onCloseClick={() => {
              setSelectedRoute(null);
              setRouteInfoPosition(null);
            }}
          >
            <div className="p-3 min-w-[220px]" dir="rtl">
              {selectedRoute === 'leg1' && leg1Info && (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-1 bg-cyan-400"></div>
                    <h3 className="font-bold text-cyan-600 text-lg">المسار الأول</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-gray-100 p-2 rounded">
                      <span className="text-gray-600 font-semibold">من:</span>
                      <span className="text-gray-800">{startPoint.name}</span>
                    </div>
                    <div className="flex items-center justify-between bg-gray-100 p-2 rounded">
                      <span className="text-gray-600 font-semibold">إلى:</span>
                      <span className="text-gray-800">{pickup.name}</span>
                    </div>
                    <div className="flex items-center justify-between bg-cyan-50 p-2 rounded">
                      <span className="text-cyan-700 font-semibold">المسافة:</span>
                      <span className="text-cyan-900 font-bold">{leg1Info.distance}</span>
                    </div>
                    <div className="flex items-center justify-between bg-cyan-50 p-2 rounded">
                      <span className="text-cyan-700 font-semibold">الوقت:</span>
                      <span className="text-cyan-900 font-bold">{leg1Info.duration}</span>
                    </div>
                  </div>
                </>
              )}
              {selectedRoute === 'leg2' && leg2Info && (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-1 bg-purple-400"></div>
                    <h3 className="font-bold text-purple-600 text-lg">المسار الثاني</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-gray-100 p-2 rounded">
                      <span className="text-gray-600 font-semibold">من:</span>
                      <span className="text-gray-800">{pickup.name}</span>
                    </div>
                    <div className="flex items-center justify-between bg-gray-100 p-2 rounded">
                      <span className="text-gray-600 font-semibold">إلى:</span>
                      <span className="text-gray-800">{destination.name}</span>
                    </div>
                    <div className="flex items-center justify-between bg-purple-50 p-2 rounded">
                      <span className="text-purple-700 font-semibold">المسافة:</span>
                      <span className="text-purple-900 font-bold">{leg2Info.distance}</span>
                    </div>
                    <div className="flex items-center justify-between bg-purple-50 p-2 rounded">
                      <span className="text-purple-700 font-semibold">الوقت:</span>
                      <span className="text-purple-900 font-bold">{leg2Info.duration}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
      </div>
    </div>
  );
}
